import { useEffect, useRef, useState } from 'react';

export default function FloatingActions() {
  const [showTop, setShowTop] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const waWrapRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ✅ Cerrar mini menú al click fuera / ESC
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!waWrapRef.current) return;
      if (!waWrapRef.current.contains(e.target)) setWaOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setWaOpen(false);
    };

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const goTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const btnBase =
    'w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ' +
    'shadow-[0_18px_55px_rgba(0,0,0,0.35)] ring-1 ring-white/20 dark:ring-white/10 ' +
    'hover:-translate-y-0.5 hover:shadow-[0_22px_70px_rgba(0,0,0,0.45)] active:translate-y-0 active:scale-[0.98]';

  // ✅ Botones del submenú: mismo look/verde WhatsApp
  const waItemBase =
    'w-full rounded-2xl px-4 py-3 text-xs font-black tracking-[0.22em] uppercase ' +
    'text-white ' +
    'bg-gradient-to-b from-[#35F07B] via-[#25D366] to-[#17B85B] ' +
    'shadow-[0_14px_45px_rgba(0,0,0,0.25)] ' +
    'ring-1 ring-white/20 dark:ring-white/10 ' +
    'hover:-translate-y-[1px] hover:shadow-[0_18px_55px_rgba(0,0,0,0.35)] active:translate-y-0 active:scale-[0.99] ' +
    'transition-all duration-200';

  const waBadge =
    'inline-flex items-center justify-center min-w-[28px] h-[28px] px-2 rounded-xl ' +
    'bg-black/15 text-white font-black text-[11px] tracking-[0.25em]';

  return (
    <div className="fixed bottom-8 right-8 z-[60] flex flex-col items-center gap-3">
      {/* ✅ Ir arriba (ARRIBA) */}
      <button
        type="button"
        onClick={goTop}
        aria-label="Ir arriba"
        title="Ir arriba"
        className={[
          btnBase,
          'text-white',
          'bg-gradient-to-b from-[#4C7BFF] via-[#3662E2] to-[#2447B8]',
          'backdrop-blur-xl',
          showTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none',
        ].join(' ')}
      >
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="18 15 12 9 6 15"></polyline>
          <line x1="12" y1="21" x2="12" y2="9"></line>
        </svg>
      </button>

      {/* ✅ WhatsApp (ABAJO) + mini menú */}
      <div ref={waWrapRef} className="relative">
        {/* Mini menú (sale hacia arriba) */}
        <div
          className={[
            'absolute right-0 bottom-[calc(100%+12px)] w-[240px]',
            'transition-all duration-200',
            waOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-2 pointer-events-none',
          ].join(' ')}
          role="menu"
          aria-label="Opciones de WhatsApp"
        >
          {/* ✅ Mantener el back del sub menú como está */}
          <div className="rounded-[1.6rem] p-2 border border-white/35 dark:border-white/10 bg-white/40 dark:bg-slate-950/30 backdrop-blur-xl shadow-[0_18px_60px_rgba(0,0,0,0.35)]">
            <div className="grid gap-2">
              {/* 1 — Información */}
              <a
                className={waItemBase}
                role="menuitem"
                href="https://wa.me/19144634702?text=Hola%2C%20quiero%20informaci%C3%B3n%20por%20favor."
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setWaOpen(false)}
              >
                <span className="flex items-center gap-3">
                  <span className={waBadge}>1</span>
                  <span className="tracking-[0.22em]">INFORMACIÓN</span>
                </span>
              </a>

              {/* 2 — Ventas */}
              <a
                className={waItemBase}
                role="menuitem"
                href="https://wa.me/19144634702?text=Hola%2C%20quiero%20hablar%20con%20ventas%20por%20favor."
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setWaOpen(false)}
              >
                <span className="flex items-center gap-3">
                  <span className={waBadge}>2</span>
                  <span className="tracking-[0.22em]">VENTAS</span>
                </span>
              </a>
            </div>
          </div>
        </div>

        {/* Botón WhatsApp */}
        <button
          type="button"
          onClick={() => setWaOpen((v) => !v)}
          aria-label="Abrir opciones de WhatsApp"
          aria-expanded={waOpen}
          aria-haspopup="menu"
          title="WhatsApp"
          className={[
            btnBase,
            'text-white',
            'bg-gradient-to-b from-[#35F07B] via-[#25D366] to-[#17B85B]',
          ].join(' ')}
        >
          <svg width="24" height="24" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
            <path d="M19.11 17.71c-.28-.14-1.64-.81-1.89-.9-.26-.1-.44-.14-.62.14-.18.28-.71.9-.87 1.08-.16.18-.33.2-.61.07-.28-.14-1.18-.43-2.24-1.38-.83-.73-1.39-1.64-1.55-1.92-.16-.28-.02-.43.12-.57.12-.12.28-.33.42-.49.14-.16.18-.28.28-.47.09-.18.05-.35-.02-.49-.07-.14-.62-1.51-.85-2.07-.22-.54-.45-.47-.62-.48-.16-.01-.35-.01-.53-.01-.19 0-.48.07-.73.35-.25.28-.96.94-.96 2.32 0 1.37.98 2.7 1.12 2.89.14.19 1.96 3 4.75 4.2.66.29 1.18.46 1.59.59.67.22 1.27.19 1.75.12.53-.08 1.64-.67 1.87-1.32.23-.65.23-1.2.16-1.32-.07-.12-.25-.19-.53-.33z" />
            <path d="M16.01 3C8.83 3 3 8.83 3 16c0 2.55.74 5.01 2.14 7.13L3.74 28.3l5.32-1.39A12.9 12.9 0 0 0 16 29c7.17 0 13-5.83 13-13S23.18 3 16.01 3zm0 23.7c-2.1 0-4.15-.56-5.93-1.63l-.43-.25-3.15.82.84-3.06-.28-.44A10.66 10.66 0 0 1 5.34 16c0-5.9 4.8-10.7 10.67-10.7 5.88 0 10.66 4.8 10.66 10.7 0 5.9-4.78 10.7-10.66 10.7z" />
          </svg>
        </button>
      </div>
    </div>
  );
}